import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import { authPost } from '../../services/apiService';

export const DATADOG_TAB_PATHS = {
  issues: '/datadog-log-analyzer?embed=1',
  coverage: '/datadog-log-analyzer/coverage?embed=1',
  alerts: '/datadog-log-analyzer/alert-coverage?embed=1'
};

const MIN_FRAME_HEIGHT = 480;

const AnalyzerFrame = ({ path, title }) => {
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(null);
  const [height, setHeight] = useState(800);
  const frameRef = useRef(null);

  // The analyzer reports its content height so the portal page is the only thing that scrolls.
  useEffect(() => {
    const onMessage = (event) => {
      const frame = frameRef.current;
      if (!frame || event.source !== frame.contentWindow || event.origin !== window.location.origin) return;
      if (event.data?.type !== 'dla-embed-height' || !Number.isFinite(event.data.height)) return;
      // A hidden (inactive-tab) frame has no width, so its reported height is meaningless.
      if (frame.offsetParent === null) return;
      setHeight(Math.max(MIN_FRAME_HEIGHT, event.data.height));
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    let cancelled = false;
    authPost('/api/datadog-log-analyzer/session', {})
      .then((res) => {
        if (cancelled) return;
        if (!res?.ticket) throw new Error('No session ticket returned');
        setSrc(`${path}&ticket=${encodeURIComponent(res.ticket)}`);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Could not open the Datadog Log Analyzer');
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  if (error) return <Alert severity="error" sx={{ maxWidth: 480 }}>{error}</Alert>;
  if (!src) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', py: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  return (
    <Box
      component="iframe"
      ref={frameRef}
      title={title}
      src={src}
      scrolling="no"
      sx={{ width: '100%', height, border: 0, display: 'block' }}
    />
  );
};

const FRAME_TITLES = {
  issues: 'Datadog Log Analyzer — Issues Dashboard',
  coverage: 'Datadog Log Analyzer — Monitor Gap Coverage',
  alerts: 'Datadog Log Analyzer — Alert Coverage'
};

// Frames stay mounted once visited so switching tabs doesn't reload the analyzer or drop its filters.
const DatadogLogAnalyzerPanel = ({ activeTab }) => {
  const [visited, setVisited] = useState(() => new Set([activeTab]));

  useEffect(() => {
    setVisited((prev) => (prev.has(activeTab) ? prev : new Set(prev).add(activeTab)));
  }, [activeTab]);

  return (
    <Box>
      {Object.keys(DATADOG_TAB_PATHS).map((id) =>
        visited.has(id) ? (
          <Box key={id} sx={{ display: activeTab === id ? 'block' : 'none' }}>
            <AnalyzerFrame path={DATADOG_TAB_PATHS[id]} title={FRAME_TITLES[id]} />
          </Box>
        ) : null
      )}
    </Box>
  );
};

export default DatadogLogAnalyzerPanel;
